import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import { CORE_CONSTANT } from '../../../../constant.ts'
import type {
  ViewQueryAggregation,
  ViewQueryDimensionFieldForMultiTablePivot,
  ViewQueryMultiTablePivot,
} from '../../../../schemas/query/index.ts'
import type { ViewUiKpi, ViewUiKpiPivotColumn } from '../../../../schemas/ui/kpi.ts'
import type {
  Metric,
  ViewConfigKpiWithMetric,
  ViewConfigPivot,
  ViewConfigSheet,
} from '../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../common.ts'
import { getMetricFromKpiFormula } from '../../getMetricFromKpiFormula.ts'
import { compileGoalConfig } from '../kpiPivot/compileGoalConfig.ts'
import { compileSheetAndMeasure } from '../sheet/compileSheetAndMeasure.ts'
import { generateDimensionFieldName } from '../sheet/measure/generateDimensionField.ts'
import { addDateFieldAndFilter } from './addDateFieldAndFilter.ts'
import { addSalescoreUserField } from './addSalescoreUserFields.ts'
import { addSalescoreUserTree } from './addSalescoreUserTree.ts'
import { generateNodeFromDimension } from './generateNodeFromDimension.ts'
import { generateUiDimensionCandidates } from './generateUiDimensionCandidates.ts'
import { updateConfigByAdditionalConfig } from './updateConfigByAdditionalConfig.ts'

export interface CompiledKpiViewConfig {
  type: 'kpi'
  queries: [ViewQueryMultiTablePivot]
  query: ViewQueryMultiTablePivot
  ui: [ViewUiKpi]
  context: CompileContext
}

export function compileKpiViewConfig(config: ViewConfigKpiWithMetric, context: CompileContext): CompiledKpiViewConfig {
  const pivot: ViewConfigPivot = context.additionalConfig?.kpiParameter?.pivot ?? {
    rows: [],
    columns: [],
  }
  const metric = getMetricFromKpiFormula(config.formula)
  if (metric === undefined) {
    context.logs.warn(`KPI設定が完了していません`)
  }
  const compiled = metric === undefined ? undefined : compileKpiViewConfigWithoutGoal(metric, pivot, context)

  const goalConfigQueries = compileGoalConfig({
    goalConfig: context.additionalConfig?.kpiParameter?.goalConfig,
    pivot,
    context,
    kpiIds: [context.resources.view.id],
  })

  const sortKeys = context.additionalConfig?.kpiParameter?.sorter?.columnKeys ?? []
  const query: ViewQueryMultiTablePivot = {
    type: 'multiTablePivot',
    tree:
      sortKeys.length > 0
        ? generateNodeFromDimension({ rows: [], columns: [] }, 0, [])
        : generateNodeFromDimension(pivot, 0, []),
    queries: [
      ...[compiled].filter(isNotNullish).map((compiled) => ({
        // kpiIdとkpiNameは、実質view.idとview.nameである。この文脈でコンパイルするときは、viewの情報がないため、指定できない
        // かつ、この文脈で使うときは指定する必要性も特にないはずなので、一旦ダミーの値を入れる
        kpiId: context.resources.view.id,
        kpiName: context.resources.view.name,
        kpiGroupName: context.resources.view.name, // 使われないはず
        // eslint-disable-next-line sonarjs/todo-tag
        name: `actual`, // TODO: もはやこの辺が機能していないかも
        role: `actual`,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        schema: compiled.query ?? null,
      })),
      ...goalConfigQueries,
    ],
    pivot: {
      rows: pivot.rows.map((dimension): ViewQueryDimensionFieldForMultiTablePivot => ({
        name: generateDimensionFieldName(dimension),
        sortedValues: dimension.sortedValues,
      })),
      columns: pivot.columns.map((dimension): ViewQueryDimensionFieldForMultiTablePivot => ({
        name: generateDimensionFieldName(dimension),
        sortedValues: dimension.sortedValues,
      })),
    },
    sorter: context.additionalConfig?.kpiParameter?.sorter,
  }

  const ui = buildUi({ config, compiled, metric, pivot })

  return {
    type: 'kpi',
    ui: [ui],
    query,
    queries: [query],
    context,
  }
}

function buildUi({
  config,
  compiled,
  metric,
  pivot,
}: {
  config: ViewConfigKpiWithMetric
  compiled: ReturnType<typeof compileKpiViewConfigWithoutGoal>
  metric: Metric | undefined
  pivot: ViewConfigPivot
}): ViewUiKpi {
  return {
    type: 'KpiSheet',
    dimensions: Array.dedupeWith(compiled?.ui.dimensions ?? [], (a, b) => a.key === b.key),
    kpi: {
      // eslint-disable-next-line sonarjs/todo-tag
      id: `kpi`, // TODO
      // eslint-disable-next-line sonarjs/todo-tag
      label: `KPI`, // TODO
      withDateField: metric?.date !== undefined,
      ...config.ui?.kpi,
    },
    // 基本的にcompile側で存在するはずなので、以下は実行されないはず
    pivot: compiled?.ui.pivot ?? {
      rows: pivot.rows.map(toUiPivotColumn),
      columns: pivot.columns.map(toUiPivotColumn),
    },
    appearance: config.ui?.appearance,
  }
}

function toUiPivotColumn(dimension: ViewConfigPivot['rows'][number]): ViewUiKpiPivotColumn {
  return {
    key: dimension.key,
    // eslint-disable-next-line sonarjs/todo-tag
    label: dimension.label, // TODO
    fieldType: dimension.key === CORE_CONSTANT.KPI_PIVOT_USER_DIMENSION().key ? 'user' : 'string',
  }
}

export function compileKpiViewConfigWithoutGoal(
  metric: Metric,
  pivot: ViewConfigPivot | undefined,
  context: CompileContext,
):
  | {
      query: ViewQueryAggregation
      ui: Pick<ViewUiKpi, 'pivot' | 'dimensions'>
    }
  | undefined {
  //
  // sheet設定に対して、additionalConfig,user,dateの適用などを行う
  // ・additionalConfig : フィルタの追加など（ダッシュボードからドリルダウンしたとき）
  // ・user : ユーザー項目のため、salescore_usersテーブルの紐付け、フィールド追加
  // ・date : 日付項目のため、日付フィールドと日付フィルタを追加（フィールドはdimensionとして使われる）
  //
  const sheetConfig: ViewConfigSheet = updateConfigByAdditionalConfig(metric.sheet, context)
  const sheetConfigWithDate = addDateFieldAndFilter(sheetConfig, metric.date)
  const sheetConfigWithUserTree = addSalescoreUserTree(sheetConfigWithDate, metric.user, context, metric.date)
  const sheetConfigWithUser = addSalescoreUserField(sheetConfigWithUserTree, metric.user, context)
  // eslint-disable-next-line sonarjs/todo-tag
  const prunedSheetConfig = pruneMeasure(sheetConfigWithUser, ``) // このロジックは過去に必要だったものであり、今は不要なはずだが、デグレが怖いのと影響範囲の調査をサボって残し続けている。 TODO: 削除

  // sheetに対してmeasureとpivotを渡して、集計用のqueryとuiを得る
  const result = compileSheetAndMeasure(
    metric.measure,
    prunedSheetConfig,
    pivot,
    metric.date === undefined
      ? undefined
      : {
          alias: CORE_CONSTANT.KPI_PIVOT_KPI_DATE_DIMENSION().key,
          property: metric.date.property,
        },
    context,
  )
  // kpiの文脈のcompileでは、必ず1つ以下のqueryが生成されるはず
  if (result === undefined) {
    return undefined
  }

  const dimensions = generateUiDimensionCandidates({ ...metric, sheet: sheetConfig }, context)
  return {
    query: result.query,
    ui: {
      ...result.ui,
      dimensions,
    },
  }
}

// 指定されたKPI用のmeasureのみを残して削除する
function pruneMeasure(config: ViewConfigSheet, measureName: string): ViewConfigSheet {
  return {
    ...config,
    fields: (config.fields ?? []).map((field) => ({
      ...field,
      measures: field.measures
        ?.filter((x) => x.name === measureName)
        .map((x) => ({
          ...x,
          name: CORE_CONSTANT.KPI_PIVOT_VALUE_COLUMN_NAME,
        })),
    })),
  }
}
